import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import PostedJobs from '../components/dashboard/PostedJobs';
import EngagementAnalytics from '../components/dashboard/EngagementAnalytics';
import Calendar from '../components/dashboard/Calendar';
import WidgetErrorBoundary from '../components/dashboard/WidgetErrorBoundary';
import useResponsive from '../hooks/useResponsive';
import MobileMessage from '../components/dashboard/MobileMessage';
import api from '../api/api';
import { Loader, RefreshCw } from 'lucide-react';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { logger } from '../utils/logger';

// No mock data - always use real data from backend

const EmployerDashboardPage = () => {
  const { isMobile } = useResponsive();
  const { user, isAuthenticated, loading: authLoading } = useContext(AuthContext);
  const navigate = useNavigate();
  const [loadStartTime] = useState(Date.now());
  
  // Manual API state to avoid useApi infinite loop bug
  const [apiData, setApiData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasFetchedRef = useRef(false);
  
  // Fetch dashboard data ONCE when authenticated
  useEffect(() => {
    const fetchDashboard = async () => {
      if (isAuthenticated && user && !authLoading && user.userType && user.id && !hasFetchedRef.current) {
        hasFetchedRef.current = true;
        setLoading(true);
        
        try {
          const response = await api.get('/dashboard');
          setApiData(response.data);
          setError(null);
        }
        catch (err) {
          logger.error('Failed to fetch dashboard:', err);
          setError(err.message || 'Failed to load dashboard');
        } finally {
          setLoading(false);
        }
      }
    };
    
    fetchDashboard();
  }, [isAuthenticated, user, authLoading]);
  
  // Refetch function
  const refetch = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/dashboard');
      setApiData(response.data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Track loading time
  useEffect(() => {
    if (!loading && apiData) {
      const loadTime = Date.now() - loadStartTime;
      logger.debug(`Dashboard loaded in ${loadTime.toFixed(2)}s`);
    }
  }, [loading, apiData, loadStartTime]);
  
  // The backend wraps the response in { success: true, data: { ... } }
  // We need to extract the nested 'data' object.
  const dashboardData = apiData?.data || null;
  
  // Log data status
  useEffect(() => {
    if (apiData && !dashboardData) {
      logger.error('Invalid dashboard data structure received:', apiData);
    }
  }, [apiData, dashboardData]);
  
  // Ensure this page is only accessible to employer users and handle auth issues
  useEffect(() => {
    // Only check after authentication is complete - don't redirect while loading
    if (authLoading || loading) { 
      logger.debug('Skipping auth check');
      return;
    }
    
    // Check if user exists and has a userType
    if (user) {
      // If user is not an employer or recruiter, redirect to appropriate dashboard
      if (user.userType !== 'employer' && user.userType !== 'recruiter') {
        navigate('/jobs');
      }
    } else if (!isAuthenticated) {
      // Only redirect if definitely not authenticated (not just API errors)

      navigate('/auth/employer');
    }
  }, [user, navigate, isAuthenticated, loading, authLoading]);
  
  // Effect to update data state when API data becomes available
  useEffect(() => {
    if (apiData) {
      if (!dashboardData) {
        logger.error('[ERROR] Invalid dashboard data structure', apiData);
      }
    }
    
    // If there's an error, log it
    if (error) {
      logger.error('API error:', error);
    }
  }, [apiData, error, dashboardData]);

  // Handle refresh with error handling
  const handleRefresh = async () => {
    setIsRefreshing(true);
    
    try {
      await refetch();
      // Success - could show toast notification here

    } catch (err) {
      logger.error('Failed to refresh dashboard data:', err);
      // Could show error toast notification here
    } finally {
      setTimeout(() => setIsRefreshing(false), 1000); // Add a slight delay for better UX
    }
  };

  if (isMobile) {
    return <MobileMessage />;
  }
  
  // Show loading skeleton on initial load
  if (loading && !apiData) {
    return (
      <div className="flex bg-white min-h-screen font-sans relative">
        <div className="fixed left-0 top-0 bottom-0 z-10">
          <Sidebar />
        </div>
        <main className="flex-1 p-8 bg-white ml-0 md:ml-24">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="grid grid-cols-4 gap-4 mb-8">
              <div className="h-24 bg-gray-200 rounded"></div>
              <div className="h-24 bg-gray-200 rounded"></div>
              <div className="h-24 bg-gray-200 rounded"></div>
              <div className="h-24 bg-gray-200 rounded"></div>
            </div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex bg-white min-h-screen font-sans relative">
      <div className="fixed left-0 top-0 bottom-0 z-10">
        <Sidebar />
      </div>
      <main className="flex-1 p-8 bg-white ml-0 md:ml-24 relative">
        {/* Show error state if no dashboard data */}
        {!dashboardData && !loading && (
          <div className="flex flex-col items-center justify-center h-64">
            <p className="text-gray-500 text-lg mb-4">Unable to load dashboard data</p>
            <button 
              className="bg-black text-white px-6 py-2 rounded-lg hover:bg-gray-800"
              onClick={handleRefresh}
            >
              Retry
            </button>
          </div>
        )}
        
        {/* Show dashboard content only when data is available */}
        {dashboardData && (
          <>
            {/* Show loading overlay only when refreshing */}
            {isRefreshing && (
              <div className="fixed inset-0 bg-white bg-opacity-70 flex items-center justify-center z-50">
                <div className="flex flex-col items-center">
                  <Loader className="animate-spin text-black" size={32} />
                  <p className="mt-2 text-black">Refreshing data...</p>
                </div>
              </div>
            )}
            
            {/* Dashboard header - pass data as props to prevent duplicate API calls */}
            <DashboardHeader 
              user={dashboardData.user} 
              stats={dashboardData.stats} 
              dashboardData={dashboardData}
              dataLoaded={!!apiData}
            />
          
          {/* Dashboard controls */}
          <div className="flex justify-between items-center mt-8 mb-6">
            <h2 className="text-xl font-bold text-black">
              Employer Dashboard Statistics
            </h2>
            <button 
              className="bg-white text-black px-4 py-2 border border-black flex items-center gap-2"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
              {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
          
          {/* Dashboard content */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <WidgetErrorBoundary widgetName="Engagement Analytics">
                <EngagementAnalytics />
              </WidgetErrorBoundary>
              
              <WidgetErrorBoundary widgetName="Posted Jobs">
                <PostedJobs />
              </WidgetErrorBoundary>
            </div>
            <div className="space-y-8">
              <WidgetErrorBoundary widgetName="Calendar">
                <Calendar />
              </WidgetErrorBoundary>
            </div>
          </div>
          </>
        )}
      </main>
    </div>
  );
};

export default EmployerDashboardPage;
